import DOMPurify from 'dompurify';
import { useState, useEffect } from 'react';
import { useAuthUser } from '../../contexts/AuthUserContext';
import { fetchAccountDetails } from '../../api/ticketData';

// Custom HTML cleaner that preserves ONLY <b> tags and text content
export const cleanDescriptionHtml = (htmlString) => {
  try {
    const sanitized = DOMPurify.sanitize(htmlString, {
      ALLOWED_TAGS: ['b', 'i', 'u', 'strong', 'em'], // Add more tags as needed
      ALLOWED_ATTR: [],    // No attributes allowed
      KEEP_CONTENT: true   // Keep text outside tags
    });

    // Extract text content while preserving <b> tags
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = sanitized;
    return tempDiv.innerHTML; // Returns content with <b> tags only
  } catch (error) {
    console.error('Error cleaning description:', error);
    return DOMPurify.sanitize(htmlString, { ALLOWED_TAGS: [] }); // Fallback to plain text
  }
};

export const priorityColor = {
    Low: 'text-green-500 bg-green-500/20',
    Medium: 'text-yellow-500 bg-yellow-500/20',
    High: 'text-red-500 bg-red-500/20'
};

export const priorityBorder = {
    Low: 'border-green-500',
    Medium: 'border-yellow-500',
    High: 'border-red-500'
};

export const categoryColor = {
    Q: 'text-yellow-500 ',
    P: 'text-error',
    F: 'text-primary',
    O: 'text-gray-500'
};

export const statusColor = {
    Open: 'text-green-500',
    'On Hold': 'text-yellow-500',
    Escalated: 'text-error',
    Resolved: 'text-green-500'
};

const TicketCard = ({ ticket }) => {
    const user = useAuthUser();

    const { 
        ticketNumber, 
        classification, 
        dueDate, 
        subject, 
        description, 
        priority, 
        email, 
        status,
        webUrl,
        accountId } = ticket;

    // Check if user is helios admin
    const isHeliosAdmin = user?.role === 'helios_admin' || user?.roles?.includes('helios_admin');

    // State for account name (only needed for admin)
    const [accountName, setAccountName] = useState(null);
    const [loadingAccount, setLoadingAccount] = useState(false);

    // Fetch account details for admin users
    useEffect(() => {
        const loadAccountDetails = async () => {
            if (!isHeliosAdmin || !accountId) {
                return;
            }

            setLoadingAccount(true);
            try {
                const accountDetails = await fetchAccountDetails(accountId);
                setAccountName(accountDetails?.accountName || null);
            } catch (error) {
                console.error('TicketCard: Failed to fetch account details:', error);
                setAccountName(null);
            } finally {
                setLoadingAccount(false);
            }
        };

        loadAccountDetails();
    }, [isHeliosAdmin, accountId]);

    // Parse the description to get just the inner content safely
    const safeDescription = cleanDescriptionHtml(description);

    // Handle card click to redirect to webUrl
    const handleCardClick = () => {
        if (webUrl) {
            window.open(webUrl, '_blank');
        }
    };

    return(
        <div
            className={`
                flex flex-col gap-2 lg:gap-3 w-full p-3 cursor-pointer border-l-2 hover:border-l-4 ${priorityBorder[priority]} transition-all duration-100 ease-out-in ticket-container hover:bg-background
                ${status === 'Closed' ? 'text-font-secondary' : ''}

            `}
            onClick={handleCardClick}
        >
            <div className="flex flex-col items-start justify-between sm:flex-row sm:items-center gap-2">
                <div className="flex flex-row items-center gap-1.5">
                    <p className="text-base font-bold lg:text-lg whitespace-nowrap truncate">{subject}</p>
                    <span className={`p-1.5 text-xs font-bold ${status === 'Closed' ? 'text-font-secondary' : priorityColor[priority]} rounded-md`}>{priority}</span> 
                </div>
                <div className="flex flex-row items-center gap-2 text-sm lg:text-base font-semibold">
                    {isHeliosAdmin ? (
                        <p className={`font-bold`}>
                            {loadingAccount ? 'Loading...' : (accountName || 'No Client Provided')}
                        </p>
                    ) : (
                        <p className="text-font-secondary">Ticket Number: <span className="font-semibold text-font-primary">{ticketNumber}</span></p>
                    )}
                </div>
            </div>
            <div className="flex flex-row justify-between items-center text-xs sm:text-sm">
                <p className="text-font-secondary">Assigned to: <span className="font-semibold text-font-primary">{email}</span></p>
                <p className="text-font-secondary">Due on: <span className="font-semibold text-font-primary">{dueDate ? new Date(dueDate).toLocaleDateString() : 'N/A'}</span></p>
            </div>
        </div>
    );
};

export default TicketCard;